import * as React from "react";
import { connect } from "react-redux";
import { DispatchFunc } from "../../../../../store/ActionTypes";
import { clearFilters, updateFilters } from "../../../../../store/FilterActions";
import {
    hasSearchableFilters,
    getFilters,
    FilterScope,
} from "../../../../../store/FilterTypes";
import { ReduxState } from "../../../../../store/ReduxState";
import { remoteTrigger, remoteClearResponse, remoteClearError } from "../../../../../store/RemoteActions";
import { PageTabs } from "../../../../../store/AppDisplays";
import { IncassoFilters } from "../../../../../store/Incassos/Types";
import { validTillValidation, validFromValidation } from "@vwpfs/mary-air-lib/utils/FormValidations";
import { ValidationStatus } from "@vwpfs/mary-air-lib/components/air/07-organisms/Form/_Types";
import { RemoteScope } from "../../../../../store/RemoteTypes";
import { ThemePalette } from "@vwpfs/mary-air-lib/theme/_Types";
import { Div } from "@vwpfs/mary-air-lib/components/air/03-base/Div";
import { Grid } from "@vwpfs/mary-air-lib/components/air/03-base/Grid";
import { Button } from "@vwpfs/mary-air-lib/components/air/05-atoms/Button";
import { Alignment, ButtonsWrapper, Orientation } from "@vwpfs/mary-air-lib/components/air/06-molecules/ButtonsWrapper";
import { Form } from "@vwpfs/mary-air-lib/components/air/07-organisms/Form";
import { FieldTypes, FormField } from "@vwpfs/mary-air-lib/components/air/07-organisms/Form/Field";

/**
 *
 */
type setFilterFn = (filters: Partial<IncassoFilters>) => void;
type clearFiltersFn = (filters?: Array<keyof IncassoFilters>) => void;

/**
 *
 */
interface OwnProps {
    loadingResults: boolean;
    defaultFilters?: IncassoFilters;
    currentTab?: PageTabs;
}

/**
 *
 */
interface StateProps {
    searchEnabled: boolean;
    filters: IncassoFilters;
}

/**
 *
 */
interface DispatchProps {
    setFilter: setFilterFn;
    clearFilters: clearFiltersFn;
    loadResults: () => void;
    clearResults: () => void;
}

interface State {
}

/**
 *
 */
type Props = OwnProps & StateProps & DispatchProps;

/**
 *
 */
export const mapStateToProps = (s: ReduxState, p: OwnProps): StateProps => ({
    searchEnabled: hasSearchableFilters(s, FilterScope.INCASSOS, p.defaultFilters),
    filters: getFilters(s, FilterScope.INCASSOS),
});

/**
 *
 */
export const mapDispatchToProps = (dispatch: DispatchFunc): DispatchProps => ({
    setFilter: (filters) => dispatch(updateFilters(filters, FilterScope.INCASSOS)),
    clearFilters: (filters) => {
        dispatch(clearFilters(filters, FilterScope.INCASSOS));
    },
    loadResults: () => dispatch(remoteTrigger(RemoteScope.INCASSO_RESULTS, { skip: 0 })),
    clearResults: () => (
        dispatch(remoteClearResponse(RemoteScope.INCASSO_RESULTS)),
        dispatch(remoteClearError(RemoteScope.INCASSO_RESULTS))
    ),
});

/**
 *
 * @param props
 */
export class FilterComp
    extends React.Component<Props, State> {

    /**
     *
     * @param props
     * @param ctx
     */
    public constructor(props: Props, ctx?: {}) {
        super(props, ctx);
        this.state = {};
        this.fromDateValidation = this.fromDateValidation.bind(this);
        this.onChangeFromDate = this.onChangeFromDate.bind(this);
        this.toDateValidation = this.toDateValidation.bind(this);
        this.onChangeToDate = this.onChangeToDate.bind(this);
        this.initFilters = this.initFilters.bind(this);
    }

    public componentDidMount() {
        this.initFilters();
    }

    public componentWillUnmount() {
        this.props.clearResults();
    }

    public render() {
        return (
            <Form onSubmit={this.onSearchFilter}>
                <Div className="scl-b-row">
                    <Grid
                        size={{ xs: 12, md: 6 }}
                    >
                        <FormField
                            label="From date"
                            placeholder="e.g. 2020/11/01"
                            popperPlacement="right"
                            onChange={this.onChangeFromDate}
                            type={FieldTypes.Date}
                            value={this.props.filters?.creationDateFrom ?? ""}
                            validationFunction={this.fromDateValidation}
                        />
                    </Grid>
                    <Grid
                        size={{ xs: 12, md: 6 }}
                    >
                        <FormField
                            label="To date"
                            placeholder="e.g. 2020/11/01"
                            popperPlacement="left"
                            onChange={this.onChangeToDate}
                            type={FieldTypes.Date}
                            value={this.props.filters?.creationDateTo ?? ""}
                            validationFunction={this.toDateValidation}
                        />
                    </Grid>
                </Div>
                <ButtonsWrapper alignment={Alignment.CENTER} defaultOrientation={Orientation.HORIZONTAL}>
                    <Button
                        className="scl-a-btn--big scl-a-btn scl-a-btn--brand-link"
                        link={{ onClick: this.initFilters }}
                        // disabled={!this.props.searchEnabled || this.props.loadingResults}
                        disabled={(!this.props.searchEnabled || this.props.loadingResults)}
                    >
                        Clear filters
                    </Button>
                    {" "}
                    <Button
                        theme={{ palette: ThemePalette.BRAND_ACCENT }}
                        className="scl-a-btn--big"
                        buttonType="submit"
                        disabled={(this.props.searchEnabled && !this.props.loadingResults)
                            ? !this.formIsValid() : false}
                    >
                        Search
                    </Button>
                </ButtonsWrapper>
            </Form>
        );
    }

    private fromDateValidation(value: string | number) {
        return validFromValidation(value.toString(),
            this.props?.filters.creationDateTo
                ? this.props.filters.creationDateTo : undefined);
    }

    private onChangeFromDate(value?: string | number) {
        return this.onChange("creationDateFrom", value?.toString() ?? "");
    }

    private toDateValidation(value: string | number) {
        return validTillValidation(value.toString(),
            this.props?.filters.creationDateFrom
                ? this.props.filters.creationDateFrom : undefined);
    }

    private onChangeToDate(value?: string | number) {
        return this.onChange("creationDateTo", value?.toString() ?? "");
    }

    private initFilters() {
        this.clearFilters();
        if (this.props.defaultFilters) {
            this.props.setFilter(this.props.defaultFilters);
        }
        this.props.loadResults();
    }

    private clearFilters = () => {
        const filters =
            (Object.keys(this.props.filters ?? {}) as Array<keyof IncassoFilters>);

        this.props.clearFilters(filters);
    };

    private onChange = <K extends keyof IncassoFilters>(key: K, value: IncassoFilters[K]) => {
        this.props.setFilter({
            [key]: value,
        });
    };

    private formIsValid = () => {
        if (this.props.filters.creationDateFrom && this.props.filters.creationDateTo) {
            return validFromValidation(this.props.filters.creationDateFrom,
                this.props.filters.creationDateTo).status === ValidationStatus.SUCCESS
                && validTillValidation(this.props.filters.creationDateTo,
                    this.props.filters.creationDateFrom).status === ValidationStatus.SUCCESS;
        }
        return true;
    };

    private onSearchFilter = () => {
        this.props.loadResults();
    };
}

/**
 *
 */
export const Filter = connect(
    mapStateToProps,
    mapDispatchToProps,
)(FilterComp);
